import { useAuth } from '@teko/auth-react';
import {
  ComboDetailPayload,
  DiscoveryProduct,
  Combo as ICombo,
  InfiniteDiscoverySearchResponse,
  PersonalizeBlockParams,
  PersonalizeBlocksResponse,
  ProductFilter,
  ProductInfoCategory,
  SWRSearchResponse,
  SearchProductResponse,
  SkusResponse,
  useDiscoverySearch as useEcomDiscoverySearch,
  useInfiniteDiscoverySearch as useEcomInfiniteDiscoverySearch,
  usePersonalizeBlocks as useEcomPersonalizeBlocks,
  useSkus as useEcomSkus,
} from '@teko/discovery';
import {
  DiscoverySearchProductResponse,
  DiscoverySearchResponse,
  SearchV2Params,
  useDiscoverySearchV2,
  useInfiniteDiscoverySearchV2,
} from '@teko/discovery/v2';
import { ProductCardProps } from '@teko/types/ecommerce';
import { WishlistButton } from 'components/WishlistButton';
import {
  useCartV2,
  useFrame,
  useRelatedProduct,
  useServices,
  useTenantConfig,
} from 'contexts';
import { createHandleEventAndCtaButton, isHomepage } from 'helpers';
import { transformBlock } from 'helpers/block';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useRef } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { SWRInfiniteConfiguration } from 'swr/infinite';
import { ObjectMap } from 'types';
import { ITEM_PER_PAGE_IN_LISTING_PAGE, SEARCH_ALGO } from '../constants';
import { useHandlerUnderEighteenYearOld } from './useHandlerUnderEighteenYearOld';
import { useRequireLoginForCartAction } from './useRequireLoginForCartAction';

const checkAlcoholicProduct = (product: DiscoveryProduct) => {
  if (!product) return product;
  let sellerCatCodes: ProductInfoCategory[] = [];
  const categories: ProductInfoCategory[] =
    product?.productInfo?.categories || [];
  const masterCategories: ProductInfoCategory[] =
    product?.productInfo?.masterCategories || [];

  sellerCatCodes = categories.concat(masterCategories);
  return {
    ...product,
    isAlcoholicCategory: sellerCatCodes.some((item) => item.isAdult),
  };
};

export const useDiscoverySearch = <T extends {} = DiscoveryProduct>(
  params: ObjectMap,
  options?: Omit<SWRConfiguration<SearchProductResponse>, 'fallbackData'> & {
    fallbackData?: DiscoverySearchProductResponse<T>;
    transformProduct?: (product: DiscoveryProduct) => T;
  },
): DiscoverySearchResponse<T> => {
  //swr turns result into null or fallbackData when validating. using ref as workaround to keep data persistent between swr validations
  const resultRef = useRef<DiscoverySearchProductResponse<T>>(
    options?.fallbackData
      ? options?.fallbackData
      : {
          products: [],
          pagination: { totalItems: 0, totalPages: 1 },
          filters: {} as ProductFilter,
        },
  );
  const { location } = useTenantConfig();
  const { discoveryService, discoveryPos365Service } = useServices();
  const { result, ...rest } = useEcomDiscoverySearch<T>(
    params?.pos365StoreCode ? discoveryPos365Service : discoveryService,
    params,
    location,
    {
      ...options,
      fallbackData: null,
      transformProduct: options?.transformProduct,
    },
  );

  const { isLoading } = rest;

  const persistentData = isLoading ? resultRef.current : result;
  resultRef.current = persistentData;

  return {
    result: {
      ...persistentData,
      products: persistentData?.products,
    },
    ...rest,
  };
};

export const useInfiniteDiscoverySearch = <T extends {} = DiscoveryProduct>(
  params: ObjectMap,
  options: SWRInfiniteConfiguration<SWRSearchResponse<T>> & {
    transformProduct?: (product: DiscoveryProduct) => T;
  },
): InfiniteDiscoverySearchResponse<T> => {
  const { discoveryService, discoveryPos365Service } = useServices();
  const { products, ...rest } = useEcomInfiniteDiscoverySearch<T>(
    params?.pos365StoreCode ? discoveryPos365Service : discoveryService,
    params,
    { ...options, transformProduct: options?.transformProduct },
  );

  return {
    products,
    ...rest,
  };
};

export const useDiscoveryProduct = (
  sku: string,
  productId?: string,
  options?: SWRConfiguration,
) => {
  const { location } = useTenantConfig();
  const { discoveryService } = useServices();
  //swr turns result into null or fallbackData when validating. using ref as workaround to keep data persistent between swr validations
  const productRef = useRef<DiscoveryProduct>(
    options?.fallbackData ? options?.fallbackData : null,
  );

  const {
    data,
    mutate,
    error,
    isValidating: isLoading,
  } = useSWR(
    !!sku ? [sku, location, productId] : null,
    async (sku: string, location: string, productId: string) => {
      const params = {
        productId,
        sku,
        location,
      };
      return discoveryService.getProduct(params);
    },
    {
      ...options,
      fallbackData: null,
      revalidateOnFocus: false,
    },
  );

  const product = isLoading || !data ? productRef.current : data;
  productRef.current = product;

  return {
    result: {
      product: product && checkAlcoholicProduct(product),
    },
    isLoading,
    isError: !!error,
    mutate,
  };
};

export const usePersonalizeBlocks = (
  blockParams: PersonalizeBlockParams,
): PersonalizeBlocksResponse => {
  const { discoveryService } = useServices();
  return useEcomPersonalizeBlocks(
    discoveryService,
    blockParams,
    transformBlock,
  );
};

export const useSkus = (skus: string[]): SkusResponse => {
  const { location } = useTenantConfig();
  const { discoveryService } = useServices();
  return useEcomSkus(discoveryService, skus, location);
};

export const useRecommendationProductsData = (sku?: string) => {
  const { discoveryService } = useServices();
  const { data, error } = useSWR(
    !!sku
      ? [`${discoveryService.getBaseUrl}/products_recommendation`, sku]
      : null,
    async (_: string, sku: string) => {
      return await discoveryService.getRecommendationProducts(sku);
    },
    {
      revalidateOnFocus: false,
    },
  );
  return {
    result: {
      productSets: data?.result?.productSets || [],
    },
    isLoading: !error && !data,
    isError: error,
  };
};

export const useRecommendationCombosData = (sku?: string) => {
  const { location } = useTenantConfig();
  const { discoveryService } = useServices();
  const { data, error } = useSWR(
    !!sku
      ? [`${discoveryService.getBaseUrl}/products_combo`, sku, location]
      : null,
    async (_: string, sku: string, location: string) => {
      return await discoveryService.getRecommendationCombos(sku, {
        location,
      });
    },
    {
      revalidateOnFocus: false,
    },
  );
  return {
    result: {
      combos: data?.result?.combos || [],
    },
    isLoading: !error && !data,
    isError: error,
  };
};

export const useRecommendationComboDetail = (
  payloadCombo: ComboDetailPayload,
  call = false,
) => {
  const { location } = useTenantConfig();
  const { discoveryService } = useServices();
  const skus = (payloadCombo?.comboLineItems || []).map((el) => el.sku);

  const { data, error } = useSWR<ICombo>(
    payloadCombo && call
      ? [
          `${discoveryService.getBaseUrl}/detail-combo`,
          JSON.stringify({ ...payloadCombo, location }),
        ]
      : null,
    (_): Promise<ICombo> => {
      return discoveryService
        .getDetailComboRecommendation({
          ...payloadCombo,
          location,
        })
        .then((res) => res?.result?.combo);
    },
    { revalidateOnFocus: false },
  );

  const reorderProductInCombo = () => {
    if (!data) return null;
    const { products } = data;
    const newProducts = skus.map((el: string) => {
      const product = products.find((p) => p.product.productInfo.sku === el);
      return product;
    });
    return {
      ...data,
      products: newProducts,
    };
  };

  return {
    result: {
      combo: reorderProductInCombo(),
    },
    isLoading: !error && !data,
    isError: error,
  };
};

// wrapper useDiscovery
export interface UseSearchReturn
  extends DiscoverySearchResponse<ProductCardProps> {
  transformProduct: (product: ProductCardProps) => ProductCardProps;
}
export const useSearch = (
  params: SearchV2Params | ObjectMap,
  fallbackData?: Partial<DiscoverySearchProductResponse<ProductCardProps>>,
  swrConfigOptions?: Omit<SWRConfiguration, 'fallbackData'>,
  searchAlgoHeaderValue?: string | null,
  showCtaButton?: boolean,
  options?: { parentOnCloseModal?: () => void },
): UseSearchReturn => {
  const {
    contactLink,
    isAllowATCFromListing,
    isDisplayUom,
    showNewProductGift,
    epicOmni1821,
  } = useTenantConfig();
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { frames } = useFrame();
  const cartV2 = useCartV2();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const { loading: authLoading } = useAuth();
  const requireLogin = useRequireLoginForCartAction();
  const router = useRouter();
  const { parentOnCloseModal } = options || {};
  const baseRelatedProductHook = useRelatedProduct();
  // When useSearch is called inside a parent modal, and pass option parentOnCloseModal to close parent modal.
  // Inject parentOnCloseModal into related product hook (selectedProduct), so it can close parent modal if needed.
  const relatedProductHook = useMemo(() => {
    if (!parentOnCloseModal) {
      return baseRelatedProductHook;
    }
    const setSelectedProduct: NonNullable<
      typeof baseRelatedProductHook.setSelectedProduct
    > = (value) => {
      if (typeof value === 'function') {
        baseRelatedProductHook.setSelectedProduct?.((prev) => {
          const nextValue = value(prev);
          return nextValue ? { ...nextValue, parentOnCloseModal } : nextValue;
        });
        return;
      }
      baseRelatedProductHook.setSelectedProduct?.(
        value ? { ...value, parentOnCloseModal } : value,
      );
    };

    return {
      ...baseRelatedProductHook,
      setSelectedProduct,
    };
  }, [baseRelatedProductHook, parentOnCloseModal]);

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing || showCtaButton,
    showCtaButtons: !!epicOmni1821,
    showCtaButtonsRelevantProducts: epicOmni1821,
    cartV2,
    frames,
    showNewProductGift,
    hideHighlightAttribute: isHomepage(router.asPath),
    // Enable tracking open in new tab (middle click or ctrl/cmd + left click)
    enableTrackOpenInNewTab:
      router.asPath.includes('/search') || router.asPath.includes('/c/'),
    relatedProductHook,
    requireLogin,
  };

  const totalCartItems = useMemo(
    () =>
      cartV2.flattenItems.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0),
    [cartV2.flattenItems],
  );

  // eslint-disable-next-line no-undef
  const transformProduct = useCallback(
    (product: ProductCardProps) => {
      return transformProductWhenUserUnder18(
        createHandleEventAndCtaButton(product, {
          ...discoveryCleanUpConfig,
          wishlistButton: { children: <WishlistButton sku={product.sku} /> },
        }),
      );
    },
    [totalCartItems, authLoading],
  );
  const defaultFallbackData: DiscoverySearchProductResponse<ProductCardProps> =
    {
      products: [],
      pagination: { totalItems: 0, totalPages: 1 },
      filters: {},
    };
  //swr turns result into null or fallbackData when validating. using ref as workaround to keep data persistent between swr validations
  const resultRef = useRef<DiscoverySearchProductResponse<ProductCardProps>>(
    fallbackData
      ? (fallbackData as DiscoverySearchProductResponse<ProductCardProps>)
      : defaultFallbackData,
  );
  const { error, loading, result } = useDiscoverySearchV2(
    discoveryServiceV2,
    { ...params } as SearchV2Params,
    {
      transform: (data) => {
        return {
          products:
            data?.products?.map((product) =>
              discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
                ...discoveryCleanUpConfig,
              }),
            ) || [],
          filters: data?.filter,
          pagination: {
            totalItems: data.total,
            totalPages: Math.ceil(data.total / ITEM_PER_PAGE_IN_LISTING_PAGE),
          },
          seoInfo: data.seoInfo,
          cookedFilters: {},
        } as DiscoverySearchProductResponse<ProductCardProps>;
      },
      ...swrConfigOptions,
      // disable most of revalidating mechanisms
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
    typeof searchAlgoHeaderValue === 'string'
      ? // remove double quote & single quote from header values
        searchAlgoHeaderValue.replace(SEARCH_ALGO.REPLACE_PATTERN, '')
      : searchAlgoHeaderValue,
  );

  const persistentData = loading
    ? resultRef.current
    : result || defaultFallbackData;
  resultRef.current = persistentData;

  const finalProducts = useMemo(
    () => persistentData?.products?.map(transformProduct),
    [persistentData.products, transformProduct],
  );

  return {
    isError: !!error,
    isLoading: loading || false,
    result: {
      ...persistentData,
      products: finalProducts,
    },
    transformProduct,
  };
};

export const useInfiniteSearch = (
  params: SearchV2Params | ObjectMap,
  fallbackData?: SWRSearchResponse<ProductCardProps>,
) => {
  const { isDisplayUom, contactLink, isAllowATCFromListing, epicOmni1821 } =
    useTenantConfig();
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { frames } = useFrame();
  const cartV2 = useCartV2();
  const { loading: authLoading } = useAuth();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const requireLogin = useRequireLoginForCartAction();

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing,
    showCtaButtons: !!epicOmni1821,
    showCtaButtonsRelevantProducts: !!epicOmni1821,
    cartV2,
    frames,
    requireLogin,
  };

  const totalCartItems = useMemo(
    () =>
      cartV2.flattenItems.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0),
    [cartV2.flattenItems],
  );

  // eslint-disable-next-line no-undef
  const transformProduct = useCallback(
    (product: ProductCardProps) => {
      return transformProductWhenUserUnder18(
        createHandleEventAndCtaButton(product, {
          ...discoveryCleanUpConfig,
          wishlistButton: { children: <WishlistButton sku={product.sku} /> },
        }),
      );
    },
    [totalCartItems, authLoading],
  );

  const {
    data,
    loadmore,
    loading: isLoading,
    ...rest
  } = useInfiniteDiscoverySearchV2(
    discoveryServiceV2,
    {
      ...params,
    },
    {
      transform: (data) => ({
        products: data.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            ...discoveryCleanUpConfig,
          }),
        ),
        total: data.total,
      }),
      fallbackData: [
        {
          products: fallbackData?.products,
          total: 0,
        },
      ],
    },
  );
  const products: ProductCardProps[] =
    data?.map((el) => el.products || []).flat() || [];

  return {
    products: products?.map(transformProduct),
    onNextPage: loadmore,
    isLoading,
    ...rest,
  };
};
